import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DeliveryNotePreferredWording } from '@work4all/components/lib/components/entity-picker/delivery-note-preferred-wording-picker/DeliveryNotePreferredWordingPicker';

import { useUser } from '@work4all/data';

import { LanguageResource } from '@work4all/utils/lib/i18n/LanguageResource.enum';
import germanBaseTrans from '@work4all/utils/lib/i18n/translation-gen/de/de-base.json';
import germanBasaltTrans from '@work4all/utils/lib/i18n/translation-gen/de/de-customer-basalt-actiengesellschaft-wiedemar.json';
import germanServiceProviderTrans from '@work4all/utils/lib/i18n/translation-gen/de/de-domain-service-provider.json';
import germanServiceProviderAndDeliveryNoteTrans from '@work4all/utils/lib/i18n/translation-gen/de/de-domain-service-provider-and-delivery-note.json';
import englishBaseTrans from '@work4all/utils/lib/i18n/translation-gen/en/en-base.json';
import englishBasaltTrans from '@work4all/utils/lib/i18n/translation-gen/en/en-customer-basalt-actiengesellschaft-wiedemar.json';
import englishServiceProviderTrans from '@work4all/utils/lib/i18n/translation-gen/en/en-domain-service-provider.json';
import englishServiceProviderAndDeliveryNoteTrans from '@work4all/utils/lib/i18n/translation-gen/en/en-domain-service-provider-and-delivery-note.json';
import dutchBaseTrans from '@work4all/utils/lib/i18n/translation-gen/nl/nl-base.json';
import dutchServiceProviderTrans from '@work4all/utils/lib/i18n/translation-gen/nl/nl-domain-service-provider.json';
import dutchServiceProviderAndDeliveryNoteTrans from '@work4all/utils/lib/i18n/translation-gen/nl/nl-domain-service-provider-and-delivery-note.json';

import { settings, useSetting } from '../settings';

const customerTranslations: Record<
  number,
  {
    en: Record<string, string>;
    de: Record<string, string>;
  }
> = {
  // Basalt Actiengesellschaft Wiedemar -> we will change this to be dealt with by a server component once all related tickets of https://work4all.atlassian.net/browse/WW-4871 are done
  '68571': {
    en: englishBasaltTrans,
    de: germanBasaltTrans,
  },
};

/**
 * The LanguageManager regulates i18n ResourceBundles depending on (currently only one) setting.
 * In the future there might be further settings regulating which translation keys should be used app-wide.
 **/

export interface LanguageManagerCtx {
  addResource: (value: LanguageResource) => void;
  removeResource: (value: LanguageResource) => void;
  resources: LanguageResource[];
}
export const LanguageManagerContext =
  React.createContext<LanguageManagerCtx | null>(null);

export const LanguageManagerProvider = (props) => {
  const [resources, setResources] = useState<LanguageResource[]>([]);

  const { i18n } = useTranslation();

  const user = useUser();
  const customerId = user.kundennummer;

  const {
    value: deliveryNotePreferredWording,
    set: setDeliveryNotePreferredWording,
  } = useSetting(settings.deliveryNotePreferredWording());

  const addResource = useCallback(
    (value: LanguageResource) => {
      switch (value) {
        case LanguageResource.ServiceProvider:
          setDeliveryNotePreferredWording(
            DeliveryNotePreferredWording.SERVICE_CONFIRMATION
          );
          break;

        case LanguageResource.ServiceProviderAndDeliveryNote:
          setDeliveryNotePreferredWording(
            DeliveryNotePreferredWording.SERVICE_CONFIRMATION_AND_DELIVERY_NOTE
          );
          break;
      }
    },
    [setDeliveryNotePreferredWording]
  );

  const removeResource = useCallback(
    (value: LanguageResource) => {
      switch (value) {
        case LanguageResource.ServiceProvider:
          setDeliveryNotePreferredWording(
            DeliveryNotePreferredWording.DELIVERY_NOTE
          );
          break;

        case LanguageResource.ServiceProviderAndDeliveryNote:
          setDeliveryNotePreferredWording(
            DeliveryNotePreferredWording.DELIVERY_NOTE
          );
          break;
      }
    },
    [setDeliveryNotePreferredWording]
  );

  const updateResourceBundles = useCallback(() => {
    let germanTrans = { ...germanBaseTrans };
    let englishTrans = { ...englishBaseTrans };
    let dutchTrans = { ...dutchBaseTrans };
    const activeResources: LanguageResource[] = [];

    const setLanguageResource = (
      resource: LanguageResource,
      resourceEnglishTrans = {},
      resourceGermanTrans = {},
      resourceDutchTrans = {}
    ) => {
      englishTrans = { ...englishTrans, ...resourceEnglishTrans };
      germanTrans = { ...germanTrans, ...resourceGermanTrans };
      dutchTrans = { ...dutchTrans, ...resourceDutchTrans };
      activeResources.push(resource);
    };

    switch (deliveryNotePreferredWording) {
      case DeliveryNotePreferredWording.SERVICE_CONFIRMATION:
        setLanguageResource(
          LanguageResource.ServiceProvider,
          englishServiceProviderTrans,
          germanServiceProviderTrans,
          dutchServiceProviderTrans
        );
        break;
      case DeliveryNotePreferredWording.SERVICE_CONFIRMATION_AND_DELIVERY_NOTE:
        setLanguageResource(
          LanguageResource.ServiceProviderAndDeliveryNote,
          englishServiceProviderAndDeliveryNoteTrans,
          germanServiceProviderAndDeliveryNoteTrans,
          dutchServiceProviderAndDeliveryNoteTrans
        );
        break;
    }

    if (customerTranslations[customerId]) {
      Object.assign(germanTrans, customerTranslations[customerId].de);
      Object.assign(englishTrans, customerTranslations[customerId].en);
    }

    setResources(activeResources);

    i18n.removeResourceBundle('de', 'translation');
    i18n.removeResourceBundle('en', 'translation');
    i18n.removeResourceBundle('nl', 'translation');

    i18n.addResourceBundle('de', 'translation', germanTrans);
    i18n.addResourceBundle('en', 'translation', englishTrans);
    i18n.addResourceBundle('nl', 'translation', dutchTrans);

    i18n.init();
  }, [customerId, i18n, deliveryNotePreferredWording]);

  useEffect(() => {
    updateResourceBundles();
  }, [updateResourceBundles]);

  return (
    <LanguageManagerContext.Provider
      value={{
        addResource,
        removeResource,
        resources,
      }}
    >
      {props.children}
    </LanguageManagerContext.Provider>
  );
};

export const useLanguageManager = () => {
  return useContext(LanguageManagerContext);
};
